<template>
	<div class="content-page content-page--beta-features-settings">
		<div ref="contentPageHeading" class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					Beta Feature Settings
				</h2>
			</div>
			<div class="content-page__actions">
				<Button
					labelText="Go Live"
					v-if="showGoLiveButton"
					:btnClick="goLive"
					:title="'Select to go live.'"
				/>
			</div>
		</div>
		<div class="beta-features-settings-content" v-show="!loading">
			<div
				ref="contentHeader"
				class="beta-features-setting-content__header"
				v-show="betaFeatureDescription"
			>
				<span class="beta-features-settings-content__header-title">
					{{ betaFeatureDescription }}
				</span>
			</div>
			<hr
				class="beta-features-settings-content__header-horizontal-line"
			/>
			<div class="styled-container">
				<p class="styled-container__title">
					Clients
				</p>
				<div class="beta-features-customers__body">
					<InfiniteScroll
						ref="contentPageMain"
						:componentData="sendComponentToInfiniteScroll"
						:extraHeightInfinityScroll="extraHeightInfinityScroll"
						@change="getSelectedFromComponent"
						@scrollEnd="handleScollEnd"
						@calculateDivMaxHeight="setCalculateDivMaxHeight"
					/>
				</div>
			</div>
		</div>
		<Spinner :processor="{ loading: loading }" />
		<transition name="slide-up">
			<div
				ref="mainContentFooter"
				class="beta-features-settings-content__footer "
				:class="{ 'is-fixed-footer': displayFooter }"
				v-show="!loading"
			>
				<div class="beta-features-settings-content__footer-container">
					<div class="beta-features-settings-content__footer-actions">
						<ButtonGroup
							:items="[
								{
									btnClick: save,
									className: 'btn--primary',
									labelText: 'Save & Close',
									disabled: hasPendingChanges
								},
								{
									btnClick: cancel,
									className: 'btn btn--link',
									labelText: 'Cancel'
								}
							]"
						/>
					</div>
				</div>
			</div>
		</transition>

		<Modals
			v-model="showGoLiveModal"
			type="confirmation"
			:title="titleGoLiveModal"
			labelConfirmButton="Yes, enable for all"
			labelCancelButton="Cancel"
			@confirm="confirmGoLive"
			@cancel="cancelGoLive"
		/>
	</div>
</template>

<script>
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Button from '@/components/forms/button/button';
import ButtonGroup from '@/components/forms/button/button-group';
import SelectList from '@/components/forms/selection-list/selection-list';
import Spinner from '@/components/visual/spinner/spinner';
import Modals from '@/components/shells/modal/modals';
import CustomerOptionsMixin from '@/mixins/customers/customer-options';
import InfiniteScroll from '@/components/shells/infinite-scroll/infinite-scroll';
import { mapActions, mapGetters } from 'vuex';

//constants
import {
	MODULE_NAME,
	SET_BETA_FEATURE,
	BETA_FEATURE,
	SAVE_BETA_FEATURE,
	GO_LIVE_BETA_FEATURE
} from '../beta-features-mutation-types';
import { CUSTOMER_OPTIONS } from '@/sections/options/options-mutation-type';

import _ from 'lodash';
export default {
	name: 'BetaFeaturesSettings',
	mixins: [CustomerOptionsMixin],
	components: {
		Breadcrumbs,
		Button,
		ButtonGroup,
		Spinner,
		Modals,
		InfiniteScroll
	},
	props: {
		id: {
			type: Number,
			require: true
		}
	},
	data() {
		return {
			selected: [],
			initialStateSelected: [],
			payload: {},
			loading: false,
			source: {},
			showGoLiveModal: false,
			titleGoLiveModal: '',
			extraHeightInfinityScroll: 0,
			displayFooter: false,
			maxHeightInfinityScroll: 0,
			mainScrollHeight: 0,
			scrollEnd: false,
			defaultUrlNames: [
				{ url: '/features', name: 'Beta Feature Manager' },
				{ name: 'Settings' }
			]
		};
	},
	computed: {
		...mapGetters(MODULE_NAME, [BETA_FEATURE]),
		sendComponentToInfiniteScroll() {
			return {
				name: 'SelectList',
				mainPath: '$parent.$refs',
				parentPath: '$parent.$parent.$refs',
				component: {
					is: SelectList,
					props: [
						{
							title: 'Clients',
							itemAvailables: this.availableItems,
							itemSelected: this.selectedItems,
							mapping: this.mappingFields,
							displayUnSelectAllButton: true
						}
					]
				}
			};
		},
		customers() {
			const { items } = this[CUSTOMER_OPTIONS];
			return items || [];
		},
		availableItems() {
			if (!this.customers.length) return [];
			return this.customers.map(item => {
				return {
					text: item.text,
					value: item.value
				};
			});
		},
		selectedItems() {
			if (
				this.customers.length &&
				_.get(this, `${BETA_FEATURE}.customerIds.length`, 0) > 0
			) {
				return this.customers
					.filter(item => {
						return this[BETA_FEATURE].customerIds.includes(
							item.value
						);
					})
					.map(item => {
						return {
							text: item.text,
							value: item.value
						};
					});
			}
			return [];
		},
		mappingFields() {
			return {
				id: 'value',
				name: 'text'
			};
		},
		betaFeatureDescription() {
			return _.get(this, `${BETA_FEATURE}.description`, '');
		},
		hasPendingChanges() {
			return _.isEqual(
				_.sortBy(this.selected),
				_.sortBy(this.initialStateSelected)
			);
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		},
		showGoLiveButton() {
			return this.validatePermission('featuresGoLive.read');
		}
	},
	async created() {
		this.loading = true;
		await this.setBetaFeature(this.id).then(hasValue => {
			if (hasValue && !_.isEmpty(this[BETA_FEATURE].customerIds)) {
				this.selected = this.selectedItems;
				this.initialStateSelected = this.selected;
			}
		});
		this.loading = false;

		this.$nextTick(() => {
			this.computeExtraHeightInfinityScroll();
		});
	},
	methods: {
		...mapActions(MODULE_NAME, {
			[SET_BETA_FEATURE]: 'setBetaFeature',
			[SAVE_BETA_FEATURE]: 'saveBetaFeature',
			[GO_LIVE_BETA_FEATURE]: 'goLiveBetaFeature'
		}),
		async goLive() {
			const self = this;
			if (!self.titleGoLiveModal) {
				self.titleGoLiveModal = `Are you sure you want to enable ${self.betaFeatureDescription} feature for all clients?`;
			}
			this.showGoLiveModal = true;
		},
		async confirmGoLive() {
			await this.goLiveBetaFeature(this.id);
			this.cancel();
		},
		cancelGoLive() {
			this.showGoLiveModal = false;
		},

		async save() {
			const self = this;
			self.parsingPayload();
			await this.saveBetaFeature(self.payload).finally(() => {
				self.cancel();
			});
		},
		cancel() {
			this.payload = {};
			this.$router.push({
				path: `/features`
			});
		},
		getSelectedFromComponent(data) {
			this.selected = data;
		},
		parsingPayload() {
			const description = this.betaFeatureDescription;
			Object.assign(this.payload, {
				id: this.id,
				description: description
			});
			this.payload.customerIds = this.selected.map(item => {
				return `${item.value}`;
			});
		},
		computeExtraHeightInfinityScroll() {
			const paddingPageHeading = 48;
			const settingContentStylePadding = 34;
			const marginHeaderHorizontalLine = 48;
			const paddingIsFixedFooter = 44;
			const containerTitle = 16;
			const avaibleAndSelectedList = 32;
			const marginSiteMain = 12;

			let contentPageHeight = Math.ceil(
				_.get(this, '$refs.contentPageHeading.offsetHeight', 0)
			);
			let contentHeaderHeight = Math.ceil(
				_.get(this, '$refs.contentHeader.offsetHeight', 0)
			);

			let extra =
				contentPageHeight +
				contentHeaderHeight +
				paddingPageHeading +
				marginHeaderHorizontalLine +
				paddingIsFixedFooter +
				settingContentStylePadding +
				containerTitle +
				avaibleAndSelectedList +
				marginSiteMain;

			this.extraHeightInfinityScroll = extra;
		},
		handleScollEnd(value) {
			this.scrollEnd = value;
			this.displayFooter = !value;
		},
		setCalculateDivMaxHeight(val) {
			this.maxHeightInfinityScroll = val.maxHeightInfinityScroll;
			this.mainScrollHeight = val.scrollHeight;
		}
	},
	watch: {
		maxHeightInfinityScroll: {
			deep: true,
			handler(val) {
				if (val && !this.displayFooter && !this.scrollEnd) {
					this.displayFooter = val < this.mainScrollHeight;
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Beta Feature Settings Page
	* Can use SCSS variables = TRUE
 */

::v-deep .selection-list__available {
	@include mq($until: md-l) {
		.selection-list__list-header {
			flex-direction: column;
			align-items: unset;

			.search {
				margin: $gutter-md 0;
			}
		}
	}
}

.content-page {
	&__heading {
		@include mq($until: sm-l) {
			flex-direction: column;
			align-items: unset;
		}
	}
	&__actions {
		@include mq($until: md) {
			margin-top: $gutter-md;
		}
	}
}

.beta-features-settings-content {
	background: $white;
	padding: $padding-md;

	@include mq($until: sm-l) {
		border-top: 1px solid $neutral-3;
	}

	@include mq($from: sm-l) {
		border: 1px solid $neutral-3;
		border-radius: 4px;
	}

	&__header {
		&-title {
			color: $black;
			font-size: $default-font-size;
			font-weight: $medium-font-weight;
		}
	}

	&__header-horizontal-line {
		@include create-horizontal-line(16px, 1px, $gutter-xl, $neutral-3);
	}

	&__footer {
		margin-top: $gutter-lg;
		padding: $padding-md 0;
		text-align: right;

		&-container {
			margin-left: $gutter-md; //matches header
			margin-right: $gutter-md; //matches header

			@include mq($from: sm-l) {
				display: flex;
				flex-wrap: wrap;
				justify-content: flex-end;
				align-items: center;
			}

			@include mq($from: lg) {
				@include contain(1200px);
			}
		}

		&-actions {
			display: inline-flex;
			align-items: initial;
		}
	}
}

.is-fixed {
	position: fixed;
	background: $white;
	bottom: 0;
	left: 0;
	width: 100%;
	text-align: right;
	@include create-top-vertical-shadow($neutral-2);
	z-index: 99999;
}

::v-deep .modal {
	&--confirmation {
		@include mq($from: md) {
			width: 600px;
		}
	}

	&__body {
		overflow: hidden;
	}

	&--full-mobile {
		max-width: 90%;
		height: auto;
		border-radius: $modal-border-radius;
	}
}
</style>
